<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useQuasar } from 'quasar'
import { formatIncidentType, formatDateTime, formatNumber } from '../../utils/format'
import type { SubmitBountyResponse } from '@roadfootage/api-client'
import { ModerationStatus } from '@roadfootage/api-client'
import { storeToRefs } from 'pinia'
import { useBountyResponseStore } from '../../stores/bounty-response'
import { useWalletStore } from '../../stores/wallet'
import { useUserStore } from '../../stores/user/user.store'
import BountyResponsesList from './BountyResponsesList.vue'

const props = defineProps<{
  bounty: SubmitBountyResponse
}>()

const emit = defineEmits<{ claimed: [bountyId: string] }>()

const $q = useQuasar()
const responseStore = useBountyResponseStore()
const walletStore = useWalletStore()
const { responsesByBounty, loadingByBounty, errorByBounty, decidingResponseId } =
  storeToRefs(responseStore)

// Why the last accept or reject failed. Cleared on the next attempt.
const decisionError = ref<string | null>(null)
// A restricted account cannot accept or reject (the API refuses it), so the buttons are not offered.
const { isRestricted } = storeToRefs(useUserStore())
const canDecide = computed(() => !props.bounty.isClaimed && !isRestricted.value)

// A bounty posted before escrow is paid from the owner's balance at accept time, so the balance may have moved.
// A failed refresh must not undo the accept.
async function refreshWallet() {
  try {
    await walletStore.fetchWallet()
  } catch (error) {
    console.error('Failed to refresh the wallet after accepting a response', error)
  }
}

async function onAccept(responseId: string) {
  const bountyId = props.bounty.id
  if (!bountyId) return
  decisionError.value = null
  const result = await responseStore.acceptResponse(bountyId, responseId)
  if (!result.ok) {
    decisionError.value = result.message
    return
  }
  $q.notify({
    type: 'positive',
    message: 'Response accepted. The points went to the uploader.',
    position: 'top'
  })
  await refreshWallet()
  emit('claimed', bountyId)
}

async function onReject(responseId: string, reason: string) {
  const bountyId = props.bounty.id
  if (!bountyId) return
  decisionError.value = null
  const result = await responseStore.rejectResponse(bountyId, responseId, reason)
  if (!result.ok) {
    decisionError.value = result.message
    return
  }
  $q.notify({ type: 'positive', message: 'Response rejected.', position: 'top' })
}

// Responses load when the poster opens the section, and again if the last attempt failed.
function onOpenResponses() {
  const id = props.bounty.id
  if (!id || responsesByBounty.value[id]) return
  void responseStore.fetchResponses(id)
}

const windowLabel = computed(() => {
  const start = formatDateTime(props.bounty.windowStartDateTime)
  const end = formatDateTime(props.bounty.windowEndDateTime)
  return `${start} — ${end}`
})
</script>

<template>
  <q-card flat bordered class="bg-white">
    <q-card-section class="q-pb-xs">
      <div class="row items-start no-wrap">
        <div class="col">
          <div class="text-body2 text-weight-bold text-blue-grey-9 ellipsis">
            {{ bounty.address?.formattedAddress || 'Location unknown' }}
          </div>
          <div class="text-caption text-blue-grey-6 q-mt-xs">
            <q-icon name="mdi-clock-outline" size="xs" class="q-mr-xs" />
            {{ windowLabel }}
          </div>
        </div>
        <q-chip
          icon="mdi-star-circle"
          color="blue-8"
          text-color="white"
          dense
          class="q-ml-sm q-mt-none"
        >
          {{ formatNumber(bounty.rewardAmountPoints ?? 0) }} pts
        </q-chip>
      </div>
    </q-card-section>

    <q-card-section class="q-pt-xs row q-gutter-xs">
      <q-chip :icon="'mdi-tag'" color="blue-grey-2" text-color="blue-grey-8" dense size="sm">
        {{ formatIncidentType(bounty.incidentReportType) }}
      </q-chip>

      <q-chip
        v-if="bounty.isClaimed"
        icon="mdi-check-circle"
        color="green-8"
        text-color="white"
        dense
        size="sm"
        data-testid="claimed-chip"
      >
        Claimed
      </q-chip>
      <q-chip
        v-else-if="bounty.expiresAt && new Date(bounty.expiresAt) < new Date()"
        :icon="'mdi-close-circle'"
        color="red-8"
        text-color="white"
        dense
        size="sm"
      >
        Expired
      </q-chip>
      <q-chip
        v-else-if="bounty.moderationStatus === ModerationStatus.Pending"
        :icon="'mdi-check-decagram-outline'"
        color="blue-8"
        text-color="white"
        dense
        size="sm"
      >
        Pending review
      </q-chip>
      <q-chip
        v-else-if="bounty.moderationStatus === ModerationStatus.Approved"
        :icon="'mdi-check-decagram'"
        color="green-8"
        text-color="white"
        dense
        size="sm"
      >
        Active
      </q-chip>
      <q-chip
        v-else-if="bounty.moderationStatus === ModerationStatus.Rejected"
        :icon="'mdi-close-circle'"
        color="red-8"
        text-color="white"
        dense
        size="sm"
      >
        Rejected
      </q-chip>
    </q-card-section>

    <q-separator />

    <q-card-section class="q-pa-sm">
      <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Description</div>
      <div class="text-caption text-blue-grey-7 ellipsis-2-lines">
        {{ bounty.requestDescription }}
      </div>
    </q-card-section>

    <q-card-section class="q-pa-sm" v-if="bounty.additionalInformation">
      <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Additional information</div>
      <div class="text-caption text-blue-grey-7 ellipsis-2-lines">
        {{ bounty.additionalInformation }}
      </div>
    </q-card-section>

    <q-separator />

    <q-expansion-item
      dense
      icon="mdi-video-outline"
      label="Responses"
      data-testid="bounty-responses"
      @show="onOpenResponses"
    >
      <div class="q-px-sm q-pb-sm">
        <bounty-responses-list
          :responses="responsesByBounty[bounty.id ?? ''] ?? []"
          :loading="loadingByBounty[bounty.id ?? ''] ?? false"
          :error="errorByBounty[bounty.id ?? ''] ?? false"
          :can-decide="canDecide"
          :reward-points="bounty.rewardAmountPoints ?? 0"
          :deciding="decidingResponseId"
          :decision-error="decisionError"
          @accept="onAccept"
          @reject="onReject"
        />
      </div>
    </q-expansion-item>
  </q-card>
</template>
